import { useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useAtomValue } from 'jotai'
import { useTranslate, authenticationState } from '@repo/unicity'
import { ActionButton, ErrorMessages, CheckoutBreadcrumb, type BreadcrumbStep } from '@/core/components'
import CheckoutLayoutSkeleton from '@/core/checkout/components/checkout-layout-skeleton.component'
import CreditCardIcon from '@/core/assets/creditcard-icons/CreditCardIcon.svg?react'
import cashOnDeliveryIcon from '@/core/assets/creditcard-icons/cash-on-delivery.png'
import { ReCaptchaV2Modal } from '@/core/recaptcha/recaptcha-v2-modal.component'
import { useRecaptchaGate } from '@/core/recaptcha/use-recaptcha-gate.hook'
import { useTaiwanPayment } from '../../hooks/useTaiwanPayment'
import { taiwanConfig } from '../../taiwan.config'
import { taiwanPaymentMethodAtom } from '../../stores/taiwan.store'
import TaiwanOrderSummary from '../checkout/components/TaiwanOrderSummary'
import TaiwanShipToSection from '../payments/components/TaiwanShipToSection'
import DuplicateOrderDialog from '../payments/components/DuplicateOrderDialog'
import RedirectPaymentGateway from '../payments/components/RedirectPaymentGateway'

const SectionCard = styled(Box)`
    border-radius: 12px;
    padding: 24px;
    background: #ffffff;
`

const LayoutGrid = styled(Box)`
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    max-width: 1152px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
    padding: 24px 20px;
    @media (min-width: 1024px) {
        grid-template-columns: minmax(0, 1fr) 368px;
        align-items: start;
    }
`

const TAIWAN_SKELETON_STEPS = [
    { key: 'customise', label: 'customise' },
    { key: 'shipping', label: 'shipping' },
    { key: 'billing', label: 'billing' },
    { key: 'purchase', label: 'purchase' }
]

const buildStepPath = (index: number, uuid: string | null, isAuthenticated: boolean): string => {
    const paths = ['/products', isAuthenticated ? '/checkout/shipping' : '/checkout/create-account', '/checkout/payment', '/checkout/review']
    const path = paths[index] ?? '/'
    return index !== 0 && uuid ? `${path}?uuid=${uuid}` : path
}

const METHOD_LABEL: Record<string, string> = {
    creditcard: 'credit_card',
    cashOnDelivery: 'cash_on_delivery'
}

export default function TaiwanReviewModule() {
    const { translate } = useTranslate()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const auth = useAtomValue(authenticationState)
    const isAuthenticated = !!(auth.token && auth.baId)
    const method = useAtomValue(taiwanPaymentMethodAtom)
    const { initialized, loadError, loading, errors, redirectForm, duplicateOrder, submitPayment, confirmDuplicateProceed, cancelDuplicate } =
        useTaiwanPayment()
    const { isVerifying: recaptchaVerifying, runWithRecaptcha, modalProps: recaptchaModalProps } = useRecaptchaGate()

    const uuid = searchParams.get('uuid')
    const submitPaymentWithRecaptcha = useCallback(() => {
        void runWithRecaptcha('checkout', submitPayment, taiwanConfig.features.checkout.recaptcha)
    }, [runWithRecaptcha, submitPayment])

    const steps: BreadcrumbStep[] = useMemo(
        () => [
            { key: 'customise', label: translate('customise') },
            { key: 'shipping', label: translate('shipping') },
            { key: 'billing', label: translate('billing') },
            { key: 'purchase', label: translate('purchase') }
        ],
        [translate]
    )

    // Handing off to the hosted gateway — render only the auto-submit form.
    if (redirectForm) {
        return <RedirectPaymentGateway data={redirectForm} data-testid="taiwan-review-redirect" />
    }

    if (!initialized) {
        return <CheckoutLayoutSkeleton steps={TAIWAN_SKELETON_STEPS} activeIndex={3} data-testid="taiwan-review-skeleton" />
    }

    return (
        <Box sx={{ width: '100%' }}>
            <CheckoutBreadcrumb
                steps={steps}
                activeIndex={3}
                onStepClick={index => navigate(buildStepPath(index, uuid, isAuthenticated))}
                sx={{ pt: '40px' }}
                data-testid="taiwan-review-breadcrumb"
            />

            <LayoutGrid>
                <Stack spacing={3} data-testid="taiwan-review-left">
                    <SectionCard data-testid="taiwan-review-section">
                        <TaiwanShipToSection />

                        <Box sx={{ borderTop: '1px solid rgba(153, 188, 223, 0.25)', mt: 2, pt: 2 }} data-testid="taiwan-review-payment-block">
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#003764' }} data-testid="taiwan-review-payment-label">
                                    {translate('payment')}:
                                </Typography>
                                <Typography
                                    onClick={() => navigate(buildStepPath(2, uuid, isAuthenticated))}
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: 400,
                                        color: '#003B6F',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        '&:hover': { color: '#003764' }
                                    }}
                                    data-testid="taiwan-review-payment-change">
                                    {translate('change')}
                                </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: 24, height: 24, flexShrink: 0 }}>
                                    {method === 'cashOnDelivery' ? (
                                        <Box component="img" src={cashOnDeliveryIcon} alt="" sx={{ width: 24, height: 24, objectFit: 'contain' }} />
                                    ) : (
                                        <CreditCardIcon width={24} height={24} />
                                    )}
                                </Box>
                                <Typography sx={{ fontSize: 14, fontWeight: 400, color: '#003764' }} data-testid="taiwan-review-payment-method">
                                    {translate(METHOD_LABEL[method] ?? 'credit_card')}
                                </Typography>
                            </Stack>
                        </Box>
                    </SectionCard>
                </Stack>

                <TaiwanOrderSummary
                    readOnly
                    footer={
                        <Stack spacing={2} sx={{ mt: 1 }} data-testid="taiwan-review-submit-box">
                            <ErrorMessages list={loadError.length > 0 ? loadError : errors} data-testid="taiwan-review-errors" />
                            <ActionButton
                                text={loading || recaptchaVerifying ? '' : translate('place_order')}
                                onClick={submitPaymentWithRecaptcha}
                                disabled={loading || recaptchaVerifying || loadError.length > 0}
                                width="100%"
                                startIcon={loading || recaptchaVerifying ? <CircularProgress size={22} color="inherit" /> : undefined}
                                data-testid="taiwan-review-submit"
                            />
                            <Typography
                                component="a"
                                href="#"
                                sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 400, color: '#003b6f', textDecoration: 'underline', textAlign: 'center' }}
                                data-testid="taiwan-review-money-back">
                                {translate('our_money_back_guarantee')}
                            </Typography>
                        </Stack>
                    }
                    data-testid="taiwan-review-order-summary"
                />
            </LayoutGrid>

            <DuplicateOrderDialog
                duplicate={duplicateOrder}
                submitting={loading}
                onProceed={confirmDuplicateProceed}
                onCancel={cancelDuplicate}
                data-testid="taiwan-review-duplicate-dialog"
            />
            <ReCaptchaV2Modal {...recaptchaModalProps} />
        </Box>
    )
}
